import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../api/client';
import { AppError } from '../api/request';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from '../onboarding/OnboardingShell';
import { postAuthPath } from '../onboarding/destination';
import { clearOnboardingDraft, saveOnboardingStep } from '../onboarding/samples';
import { panelPath } from '../config/paths';

/** Final onboarding step — framed as publish, not payment scare. */
export default function SelectPlanPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<api.Plan[]>([]);
  const [error, setError] = useState('');
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (!user?.onboardingCompleted || user.hasActiveAccess) return;
    saveOnboardingStep('/select-plan', user.id);
  }, [user?.id, user?.onboardingCompleted, user?.hasActiveAccess]);

  useEffect(() => {
    if (!user) return;
    if (!user.onboardingCompleted) {
      navigate(postAuthPath(user), { replace: true });
      return;
    }
    if (user.hasSubscription || (user.hasActiveAccess && user.planId)) {
      clearOnboardingDraft();
      navigate(panelPath('/'), { replace: true });
      return;
    }
    api
      .listPlans()
      .then((list) => {
        setPlans(list);
        if (list.length > 0) setSelected((prev) => prev || list.find((p) => p.id === 'growth')?.id || list[0].id);
      })
      .catch(() => setError(t('selectPlan.loadError')));
  }, [user, navigate, t]);

  async function choose(planId: string) {
    setError('');
    setCheckingOut(planId);
    try {
      const url = await api.createCheckout(planId);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('selectPlan.checkoutError'));
      setCheckingOut(null);
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#1c1420] text-[#c9b8d1]">
        {t('common.loading')}
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!user.onboardingCompleted) {
    return <Navigate to={postAuthPath(user)} replace />;
  }

  return (
    <OnboardingShell title={t('selectPlan.title')} maxWidth={900}>
      <div className="mb-10 text-center">
        <div className="mb-3 text-xs font-semibold text-[#ff9a6c]">{t('onboarding.stepOf', { current: 4, total: 4 })}</div>
        <h2 className="m-0 mb-2 text-[36px] font-extrabold tracking-[-0.02em]">{t('selectPlan.title')}</h2>
        <p className="m-0 text-sm text-[#c9b8d1]">{t('selectPlan.subtitle')}</p>
      </div>

      {error && <p className="mb-4 text-center text-sm text-[#ff8a9e]">{error}</p>}

      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {plans.map((p) => {
          const active = selected === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p.id)}
              className={`rounded-xl border-2 p-7 text-left transition-all ${
                active ? 'border-[#ff6f91] bg-[#3a1a2a]' : 'border-[#2b1f33] bg-[#221733]'
              }`}
            >
              <div className="mb-1 text-lg font-bold">{p.name}</div>
              <div className="mb-3 text-[28px] font-extrabold text-[#ff6f91]">
                ${p.monthlyPriceUsd}
                <span className="text-[13px] font-normal text-[#a8899f]">{t('home.perMonth')}</span>
              </div>
              <ul className="m-0 list-disc space-y-1 pl-5 text-[13px] leading-[1.8] text-[#c9b8d1]">
                <li>{t('home.igAccounts', { count: p.maxInstagramAccounts })}</li>
                <li>{t('home.monthlyCredits', { count: p.monthlyAiCredits })}</li>
              </ul>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Link
          to="/onboarding/connect"
          className="flex-1 rounded-lg border border-[#3d2e46] bg-transparent py-3 text-center text-sm font-semibold text-[#f5eef7]"
        >
          {t('onboarding.back')}
        </Link>
        <button
          type="button"
          onClick={() => selected && void choose(selected)}
          disabled={!selected || checkingOut !== null}
          className="flex-1 rounded-lg border-none bg-[#ff6f91] py-3 text-sm font-semibold text-[#2b0e18] hover:bg-[#ff85a3] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {checkingOut ? t('common.redirecting') : t('selectPlan.choosePlan')}
        </button>
      </div>
    </OnboardingShell>
  );
}
